import clsx from 'clsx'
import { forwardRef } from 'react'

import { useTabsContext } from './context'

interface ListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  children: React.ReactNode;
  border?: boolean;
}

function findNextTab (element: HTMLElement | null) {
  if (!element) return null

  let sibling = element.nextElementSibling

  while (sibling) {
    if (sibling.matches('[data-tab]')) {
      const enabledTab = sibling.querySelector(
        '[role="tab"]:not(:disabled)'
      ) as HTMLElement | null

      if (enabledTab) return enabledTab
    }
    sibling = sibling.nextElementSibling
  }

  return null
}

function findPreviousTab (element: HTMLElement | null) {
  if (!element) return null

  let sibling = element.previousElementSibling

  while (sibling) {
    if (sibling.matches('[data-tab]')) {
      const enabledTab = sibling.querySelector(
        '[role="tab"]:not(:disabled)'
      ) as HTMLElement | null

      if (enabledTab) return enabledTab
    }
    sibling = sibling.previousElementSibling
  }

  return null
}

function getFocusedTab (element: HTMLElement) {
  return element.querySelector('[data-tab]:focus-within') as HTMLElement | null
}

function getNextTab (element: HTMLElement) {
  const activeTab = getFocusedTab(element)

  return findNextTab(activeTab)
}

function getPreviousTab (element: HTMLElement) {
  const activeTab = getFocusedTab(element)

  return findPreviousTab(activeTab)
}

export const List = forwardRef<HTMLDivElement, ListProps>(
  ({ children, border = true, ...props }, forwardedRef) => {
    const { activationMode, setActiveTab, tabAlignment } = useTabsContext()

    function selectTab (element: HTMLElement | null) {
      if (element) {
        element.focus({ preventScroll: true })
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
          inline: 'nearest',
        })

        if (activationMode === 'automatic') {
          setActiveTab(element?.dataset.name as string)
        }
      }
    }

    function handleKeyDown (event: React.KeyboardEvent<HTMLDivElement>) {
      const { currentTarget } = event
      let handled = false

      switch (event.key) {
        case 'ArrowLeft':
          handled = true
          selectTab(getPreviousTab(currentTarget))
          break
        case 'ArrowRight':
          handled = true
          selectTab(getNextTab(currentTarget))
          break
      }

      if (handled) event.preventDefault()
    }

    return (
      <div
        {...props}
        className={clsx(
          'peer/tab-list flex items-end gap-4 overflow-x-auto -mb-px',
          {
            '-mx-2': tabAlignment === 'content-box',
            'shadow-[0_-1px_0_0_inset] shadow-gray-200': border,
          }
        )}
        onKeyDown={handleKeyDown}
        ref={forwardedRef}
        role='tablist'
      >
        {children}
      </div>
    )
  }
)

List.displayName = 'Tabs.List'
